<template>
  <div class="admin-page">
    <div class="admin-container">
      <div class="admin-header">
        <div class="admin-header__titles">
          <h1 class="admin-title"><i class="fa-solid fa-list-check"></i> Service Audit</h1>
          <p class="admin-subtitle">Review the change history for any posting on the platform.</p>
        </div>
        <div class="admin-header__actions">
          <!-- Shown when this page was opened by drilling into one service from
               All Services, which previously left no way back to that list. -->
          <button
            v-if="cameFromAllServices"
            class="admin-btn admin-btn--outline"
            @click="backToAllServices"
          >
            <i class="fa-solid fa-arrow-left"></i> Back to All Services
          </button>
          <button class="admin-btn admin-btn--ghost" @click="$router.push('/')">
            <i class="fa-solid fa-house"></i> Go to Site
          </button>
        </div>
      </div>

      <div class="admin-card admin-section">
        <div class="admin-toolbar filters">
          <div class="admin-field service-id-field">
            <label class="admin-label">Service ID (optional)</label>
            <input class="admin-input" v-model="servicesID" placeholder="Leave blank for all audits" />
          </div>
          <div class="admin-field">
            <label class="admin-label">From Date</label>
            <DatePicker v-model="filterDateFrom" :max="filterDateTo" placeholder="Any start date" />
          </div>
          <div class="admin-field">
            <label class="admin-label">To Date</label>
            <DatePicker v-model="filterDateTo" :min="filterDateFrom" placeholder="Any end date" />
          </div>
          <div class="admin-field">
            <label class="admin-label">Sort By</label>
            <SearchableSelect
              v-model="sortBy"
              :options="sortOptions"
              :clearable="false"
              placeholder="Sort by"
              @change="currentPage = 1; fetchAudit()"
            />
          </div>
          <button class="admin-btn admin-btn--primary" @click="currentPage = 1; fetchAudit()" :disabled="loading">
            <i class="fa-solid fa-magnifying-glass"></i> Load
          </button>
          <button class="admin-btn admin-btn--outline" @click="currentPage = 1; fetchAudit()" :disabled="loading">
            <i class="fa-solid fa-filter"></i> Apply Filter
          </button>
          <button class="admin-btn admin-btn--outline" @click="clearFilters" :disabled="loading">Clear</button>
        </div>
      </div>

      <div v-if="error" class="admin-badge admin-badge--danger admin-section">{{ error }}</div>

      <TableSkeleton v-if="loading" :columns="5" />

      <div v-if="items.length" class="admin-table-wrap">
        <table class="admin-table audit-table">
          <colgroup>
            <col class="c-action" />
            <col class="c-service" />
            <col class="c-user" />
            <col class="c-changes" />
            <col class="c-status" />
          </colgroup>
          <thead>
            <tr>
              <!-- "Action" here is the audited event (create / update / delete),
                   NOT a column of buttons. Deliberately singular so it does not
                   read as the "Actions" column every other report uses. -->
              <th>Action</th>
              <th>Service</th>
              <th>Owner</th>
              <th>Changes</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.auditID">
              <td class="cell-stack">
                <span class="cell-stack__main"><span class="admin-badge admin-badge--info">{{ item.action }}</span></span>
                <span class="cell-stack__sub is-time" :title="formatTs(item.timestamp)">{{ formatTs(item.timestamp) }}</span>
              </td>
              <td class="service-cell">
                <a
                  v-if="postUrl(item)"
                  :href="postUrl(item)"
                  target="_blank"
                  rel="noopener"
                  class="service-link"
                  :title="item.servicesName"
                >{{ item.servicesName }}</a>
                <span v-else class="ellip" :class="{ 'admin-muted': !item.servicesName }" :title="item.servicesName || item.servicesID">{{ item.servicesName || 'Unknown' }}</span>
                <div class="service-id ellip" :title="item.servicesID">{{ item.servicesID || '-' }}</div>
              </td>
              <td class="cell-stack">
                <span class="cell-stack__main ellip">
                  <UserActivityLink :user-id="item.userID" :label="ownerName(item)" :title="ownerTitle(item)" />
                </span>
                <span class="cell-stack__sub is-ip ellip" :title="item.ipAddress || '-'">{{ item.ipAddress || '-' }}</span>
                <span class="cell-stack__sub ellip" :title="item.userAgent || '-'">{{ item.userAgent || '-' }}</span>
              </td>
              <td class="changes-cell">
                <div v-if="item.action === 'EDIT' && item.changes && Object.keys(item.changes).length">
                  <div v-for="(chg, key) in item.changes" :key="key" class="change-row">
                    <strong class="change-key">{{ key }}:</strong>
                    <span class="admin-badge admin-badge--danger from" :title="renderVal(chg.from)">{{ renderVal(chg.from) }}</span>
                    <i class="fa-solid fa-arrow-right change-arrow"></i>
                    <span class="admin-badge admin-badge--success to" :title="renderVal(chg.to)">{{ renderVal(chg.to) }}</span>
                  </div>
                </div>
                <div v-else-if="item.action === 'ADD'">
                  <span class="admin-badge admin-badge--success">Created</span>
                </div>
                <div v-else-if="item.action === 'DELETE'">
                  <span class="admin-badge admin-badge--danger">Deleted</span>
                </div>
                <div v-else class="admin-muted">-</div>
              </td>
              <td>
                <span
                  class="admin-badge"
                  :class="{ 'admin-badge--success': item.status === 'success', 'admin-badge--danger': item.status === 'failed' }"
                >
                  {{ item.status }}
                </span>
                <div v-if="item.errorMessage" class="err" :title="item.errorMessage">{{ item.errorMessage }}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="items.length" class="admin-pagination">
        <button class="admin-btn admin-btn--outline admin-btn--sm" @click="prevPage" :disabled="currentPage === 1">← Previous</button>
        <span class="admin-pagination__info">Page {{ currentPage }} of {{ totalPages }} ({{ totalCount }} total)</span>
        <button class="admin-btn admin-btn--outline admin-btn--sm" @click="nextPage" :disabled="currentPage >= totalPages">Next →</button>
      </div>

      <div v-else-if="!loading && servicesID" class="admin-empty">
        <i class="fa-solid fa-clock-rotate-left"></i>
        <p class="admin-empty__title">No audit entries</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import TableSkeleton from "@/components/common/TableSkeleton.vue";
import DatePicker from "@/components/common/DatePicker.vue";
import SearchableSelect from '@/components/pages/Directory/SearchableSelect.vue';
import { ref, computed, onMounted } from 'vue';
import axiosInst from '@/utils/axiosinstance';
import { startOfDayISO, endOfDayISO } from '@/utils/dateRange';
import { useRoute, useRouter } from 'vue-router';
import UserActivityLink from '@/components/common/UserActivityLink.vue';
// Shared with the user-activity reports so the slug rule lives in one place.
import { postUrl } from '@/utils/postUrl';

const sortOptions = [
  { value: 'newest', label: 'Created (newest first)' },
  { value: 'oldest', label: 'Created (oldest first)' },
];

const route = useRoute();
const router = useRouter();
const servicesID = ref(route.query.servicesID || route.params.servicesID || '');
const items = ref([]);
const loading = ref(false);
const error = ref('');
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);
const limit = ref(100);
const filterDateFrom = ref('');
const filterDateTo = ref('');
const sortBy = ref('newest');

// ── Back to All Services ─────────────────────────────────────────────────────
// Arriving here with a servicesID means an admin clicked Audit on one row of
// All Services; the page had no way back, so they lost the email/slug filter
// they had typed. All Services keeps that filter in the URL under `as*` keys,
// which ride along through this page — passing them back restores the list
// exactly as they left it, on the same page of results.
const cameFromAllServices = computed(
  () => !!(route.query.servicesID || route.params.servicesID)
);

function backToAllServices() {
  // Drop servicesID: it targets this audit view, and inside the console it
  // would otherwise keep re-keying the All Services pane.
  const { servicesID: _drop, ...rest } = route.query;
  if (route.name === 'admin-console') {
    router.push({ query: { ...rest, section: 'all-services' } });
  } else {
    router.push({ name: 'all-services', query: rest });
  }
}

function goToDashboard() {
  router.push('/');
}

function clearFilters() {
  filterDateFrom.value = '';
  filterDateTo.value = '';
  sortBy.value = 'newest';
  currentPage.value = 1;
  fetchAudit();
}

function formatTs(ts) {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts || '-';
  }
}

function renderVal(v) {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Listing owner's identity. performedBy (the actor) is not reliably captured
// on audit records, so fall back to the owner resolved from userID.
function ownerName(item) {
  return item.ownerUserName || item.performedBy || item.ownerEmail || '-';
}
function ownerTitle(item) {
  return [item.ownerUserName, item.ownerEmail, item.userID].filter(Boolean).join(' · ') || '-';
}


async function fetchAudit() {
  error.value = '';
  loading.value = true;
  try {
    const payload = {
      servicesID: servicesID.value,
      limit: limit.value,
      page: currentPage.value,
      sortBy: sortBy.value,
    };

    // Date-only inputs: widen each day to its full local span so that picking
    // the same day for From and To returns that whole day, not just midnight.
    const dateFrom = startOfDayISO(filterDateFrom.value);
    const dateTo = endOfDayISO(filterDateTo.value);
    if (dateFrom) payload.dateFrom = dateFrom;
    if (dateTo) payload.dateTo = dateTo;

    const { data } = await axiosInst.post('/api/serviceaudit/byservice', payload);
    items.value = data.items || [];
    currentPage.value = data.page || 1;
    totalPages.value = data.totalPages || 1;
    totalCount.value = data.totalCount || 0;
  } catch (e) {
    error.value = e?.message || 'Failed to load audit';
  } finally {
    loading.value = false;
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    fetchAudit();
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchAudit();
  }
}

onMounted(() => {
  // Load all audits by default, or specific service if provided
  fetchAudit();
});
</script>

<style scoped>
/* Page-specific tweaks only — shared look comes from admin-theme.css */
.filters { align-items: flex-end; }
.filters .admin-field { gap: 4px; }
.service-id-field { min-width: 320px; }
.change-key { color: var(--g-text-secondary); font-weight: 500; }
.change-arrow { color: var(--g-text-disabled); font-size: 12.5px; }
.err { color: var(--g-red); font-size: 12.5px; margin-top: 4px; }

/* ── Compact, fixed layout so every column fits the card width with no
   horizontal scroll. Long values truncate with an ellipsis + native tooltip
   (title attr) so nothing is lost. ── */
.audit-table { table-layout: fixed; width: 100%; }
.audit-table th, .audit-table td {
  padding: 8px 10px;          /* tighter than the default 12-14px */
  font-size: 13px;
  vertical-align: top;
}
.audit-table thead th { padding: 9px 10px; }

/* Column widths (sum = 100%). */
/* Five merged columns replace the old eight — timestamp folded under Action,
   IP and user agent under Owner. */
.c-action  { width: 13%; }
.c-service { width: 24%; }
.c-user    { width: 20%; }
.c-changes { width: 32%; }
.c-status  { width: 11%; }

/* Single-line cells truncate cleanly. */
.ellip { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nowrap { white-space: nowrap; }

/* Service column: name link on top, id muted below — both truncate. */
.service-cell { line-height: 1.3; }
.service-link {
  color: var(--g-blue, #1a73e8);
  font-weight: 500;
  text-decoration: none;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.service-link:hover { text-decoration: underline; }
.service-id { color: var(--g-text-disabled); font-size: 12px; margin-top: 2px; }

/* Changes column: stack each change, wrap/break long values within the column. */
.changes-cell { white-space: normal; }
.change-row { display: flex; align-items: center; flex-wrap: wrap; gap: 5px; margin-bottom: 5px; }
.change-row:last-child { margin-bottom: 0; }
.change-row .from,
.change-row .to {
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
